import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  ASSISTANT_COLUMN_CLASS,
  ASSISTANT_CONTROLS_CLEARANCE,
  ASSISTANT_PANE_COLUMN_CLASS,
  ASSISTANT_PANE_TOP_CLASS,
  AssistantComposerFrame,
  AssistantControls,
  AssistantPaneFoot,
  AssistantWorkspace,
} from './assistant-surface'

/*
 * The places' shapes while the chat's chunk and its first reads are on the wire, built from the frame's own pieces so
 * the chat lands in their boxes (docs/notes/app/loading-states.md). Eager: the section panes and the slot route draw them.
 */

/** The pane while the chat's chunk and its first reads are on the wire: its foot, the controls over the field. */
export function PaneSkeleton(props: { children?: ReactNode }) {
  return (
    <div aria-busy="true" className={ASSISTANT_PANE_COLUMN_CLASS}>
      {props.children}
      <AssistantPaneFoot controls={<ControlsSkeleton count={2} />}>
        <ComposerSkeleton />
      </AssistantPaneFoot>
    </div>
  )
}

/** The chat's control icons (`ControlButton`, size icon-sm) before they work. */
export function ControlsSkeleton(props: { count: number }) {
  return Array.from({ length: props.count }, (_, index) => <Skeleton key={index} className="size-7 rounded-md" />)
}

/** The composer's field (`ASSISTANT_INPUT_CLASS`'s min-h-10, rounded-lg, 2px outline) in its frame, before it takes input. */
export function ComposerSkeleton() {
  return (
    <AssistantComposerFrame>
      <div className="border-input bg-background h-10 w-full rounded-lg border-2" />
    </AssistantComposerFrame>
  )
}

/**
 * A place's shape while the chat's chunk and its first reads are on the wire: its controls, the history beside it
 * when wide, `children` where the conversation starts, the composer at its foot.
 */
export function AssistantSkeleton(props: {
  wide?: boolean
  controls?: number
  onClose?: () => void
  children?: ReactNode
}) {
  return (
    <div aria-busy="true" className="relative flex min-h-0 flex-1 flex-col">
      <AssistantControls onClose={props.onClose}>
        <ControlsSkeleton count={props.controls ?? (props.wide ? 1 : 2)} />
      </AssistantControls>
      <AssistantWorkspace
        wide={props.wide ?? false}
        history={
          <div className="flex flex-col gap-1 p-2">
            {['72%', '58%', '84%'].map((width) => (
              <div key={width} className="flex flex-col gap-1.5 px-2.5 py-1.5">
                <Skeleton className="h-4" style={{ width }} />
                <Skeleton className="h-3 w-28" />
              </div>
            ))}
          </div>
        }
      >
        <div className="min-h-0 flex-1 overflow-hidden">{props.children}</div>
        <ComposerSkeleton />
      </AssistantWorkspace>
    </div>
  )
}

/** A stored conversation's outline while it is read: a question, and the lines of its answer. */
export function ThreadSkeleton(props: { pageScroll?: boolean }) {
  return (
    <div
      className={cn(
        ASSISTANT_COLUMN_CLASS,
        'flex flex-col gap-4 pb-4',
        props.pageScroll ? cn('px-2', ASSISTANT_PANE_TOP_CLASS) : cn('px-4', ASSISTANT_CONTROLS_CLEARANCE),
      )}
    >
      <Skeleton className="h-9 w-2/5 self-end rounded-xl" />
      <div className="flex flex-col gap-2">
        {['92%', '84%', '61%'].map((width) => (
          <Skeleton key={width} className="h-4" style={{ width }} />
        ))}
      </div>
    </div>
  )
}
